import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { stories, levels, languages } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';
import { BookOpen, Clock, Search, Filter, ArrowLeft, Star } from 'lucide-react';

const StoryBrowser = () => {
  const navigate = useNavigate();
  const { currentLanguage, currentLevel, getLanguageInfo } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  const filteredStories = useMemo(() => {
    let filtered = stories.filter(story => story.language === currentLanguage);

    if (searchTerm) {
      filtered = filtered.filter(story => 
        story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        story.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedLevel !== 'all') {
      filtered = filtered.filter(story => story.level === selectedLevel);
    }

    // Sort stories
    switch (sortBy) {
      case 'level':
        filtered.sort((a, b) => {
          const levelOrder = ['A1', 'A2', 'B1', 'B2', 'C1'];
          return levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level);
        });
        break;
      case 'title':
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      default: // recent
        filtered.sort((a, b) => b.id - a.id);
    }

    return filtered;
  }, [currentLanguage, searchTerm, selectedLevel, sortBy]);

  const handleStoryClick = (storyId) => {
    navigate(`/story/${storyId}`);
  };

  const getLevelColor = (level) => {
    const colors = {
      'A1': 'bg-green-100 text-green-800',
      'A2': 'bg-blue-100 text-blue-800',
      'B1': 'bg-yellow-100 text-yellow-800',
      'B2': 'bg-orange-100 text-orange-800',
      'C1': 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const currentLangInfo = getLanguageInfo(currentLanguage);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="p-2"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <BookOpen className="h-8 w-8 text-[#4ECDC4]" />
                <span className="text-2xl font-bold text-slate-900">DMV-Lernportal</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-lg">{currentLangInfo.flag}</span>
                <span className="font-medium">{currentLangInfo.name}</span>
              </div>
              <Button 
                variant="outline"
                onClick={() => navigate('/training')}
                className="border-[#4ECDC4] text-[#4ECDC4] hover:bg-[#4ECDC4] hover:text-white"
              >
                Training
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Suche nach Storys..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Alle Niveaus" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Niveaus</SelectItem>
                  {levels.map((level) => (
                    <SelectItem key={level.code} value={level.code}>
                      {level.code} - {level.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Sortieren nach" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Neueste zuerst</SelectItem>
                  <SelectItem value="level">Nach Niveau</SelectItem>
                  <SelectItem value="title">Alphabetisch</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Story Grid */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-slate-900">
              Geschichten für {currentLangInfo.name}
            </h1>
            <div className="text-slate-600">
              {filteredStories.length} Geschichten gefunden
            </div>
          </div>

          {filteredStories.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                Keine Geschichten gefunden
              </h3>
              <p className="text-slate-600">
                Versuche deine Filter zu ändern oder einen anderen Suchbegriff zu verwenden.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStories.map((story) => (
                <Card 
                  key={story.id}
                  className="cursor-pointer hover:shadow-lg transition-all hover:scale-105 border-0 shadow-md overflow-hidden"
                  onClick={() => handleStoryClick(story.id)}
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img 
                      src={story.image} 
                      alt={story.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2">
                      <Badge className={getLevelColor(story.level)}>
                        {story.level}
                      </Badge>
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <div className="space-y-3">
                      <h3 className="font-bold text-slate-900 line-clamp-2 leading-tight">
                        {story.title}
                      </h3>
                      <p className="text-slate-600 text-sm line-clamp-3">
                        {story.description}
                      </p>
                      
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {story.readTime}
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          <span>4.8</span>
                        </div>
                      </div>

                      {story.grammarFocus && (
                        <div className="flex flex-wrap gap-1">
                          {story.grammarFocus.slice(0, 2).map((topic, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {topic}
                            </Badge>
                          ))}
                          {story.grammarFocus.length > 2 && (
                            <Badge variant="secondary" className="text-xs">
                              +{story.grammarFocus.length - 2}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Add more stories section */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <BookOpen className="h-12 w-12 text-[#4ECDC4] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Mehr Geschichten kommen bald!
            </h3>
            <p className="text-slate-600 mb-6">
              Wir fügen regelmäßig neue, spannende Geschichten für alle Niveaus hinzu.
            </p>
            <Button 
              className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
              onClick={() => navigate('/training')}
            >
              Meine gelernten Wörter trainieren
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryBrowser;